import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FaceLandmarker } from '@mediapipe/tasks-vision';
import type {
  ArgusCaptureQcSegment,
  ArgusCaptureQcV1,
  ArgusCaptureTelemetryV1,
  ArgusRuntimeTelemetryEventV1,
  ArgusStimulusTimingV1,
  EyeTraxSample,
} from '../types';
import { useSession } from '../store/session';
import { loadGalleryConfig, MediaItem } from '../lib/gallery';
import { forceFaceDelegate, getActiveFaceDelegate, getFaceLandmarker } from '../lib/face';
import {
  computeFaceBoundsList,
  pickFaceIndexByAnchor,
  pickLargestFaceIndex,
  type FaceAnchor,
} from '../lib/faceSelection';
import { Alert, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { submitForm } from '../api';
import { openOrCreateDB, saveVideoArtifact, MyDB } from '../runner/openIDB';
import { toPositiveInt } from '../runner/uploadUtils';
import type { IDBPDatabase } from 'idb';
import {
  buildArgusRunnerPath,
  isVideoWorkflow,
  persistArgusWorkflow,
  resolveArgusWorkflow,
} from '../lib/workflow';
import { prepareGalleryStimuli } from '../lib/stimulusPreload';
import { getOrCreateClientRunId } from '../lib/clientRun';

const DEBUG_ENABLED = process.env.NODE_ENV !== 'production';
const CAPTURE_DEBUG_FLAG = String(import.meta.env.VITE_CAPTURE_DEBUG ?? '').toLowerCase();
const CAPTURE_DEBUG =
  CAPTURE_DEBUG_FLAG === '1' || CAPTURE_DEBUG_FLAG === 'true' || CAPTURE_DEBUG_FLAG === 'yes';
const STREAM_DURING_TEST_FLAG = String(
  import.meta.env.VITE_UPLOAD_STREAM_DURING_TEST ?? '',
).toLowerCase();
const STREAM_UPLOAD_DURING_TEST =
  STREAM_DURING_TEST_FLAG === '' ||
  STREAM_DURING_TEST_FLAG === '1' ||
  STREAM_DURING_TEST_FLAG === 'true' ||
  STREAM_DURING_TEST_FLAG === 'yes' ||
  STREAM_DURING_TEST_FLAG === 'on';
const DB_NAME = 'my-db';
const IDB_BATCH_SIZE = toPositiveInt(import.meta.env.VITE_UPLOAD_BATCH_SIZE, 250);
const FLUSH_INTERVAL_MS = toPositiveInt(import.meta.env.VITE_UPLOAD_FLUSH_INTERVAL_MS, 1000);
const MAX_WRITE_RETRIES = 5;
const WRITE_RETRY_DELAY_MS = 200;
const WORKER_STOP_TIMEOUT_MS = 2000;
const RECORDER_STOP_TIMEOUT_MS = 5000;
const FRAME_GAP_THRESHOLD_MS = 250;
const RAF_GAP_THRESHOLD_MS = 500;
const QC_FACE_LOST_OVERLAY_MS = 1500;
const QC_MIN_FACE_PRESENT_RATIO = 0.8;
const QC_MAX_NO_FACE_MS = 10_000;
const QC_MIN_SEGMENT_FACE_RATIO = 0.6;
const QC_MAX_FAILED_SEGMENT_RATIO = 1;

type WorkerMessage = { type?: string; [key: string]: unknown };
type PendingRecord = MyDB['landmarks']['value'];
type QcSegmentAccumulator = {
  mediaIndex: number;
  totalFrames: number;
  facePresentFrames: number;
};
type QcAccumulator = {
  totalFrames: number;
  facePresentFrames: number;
  multiFaceFrames: number;
  longestNoFaceMs: number;
  currentNoFaceStartMs: number | null;
  segments: Map<number, QcSegmentAccumulator>;
};
type CameraFrameMetadata = {
  mediaTime?: number;
  presentedFrames?: number;
  expectedDisplayTime?: number;
  width?: number;
  height?: number;
};
type VideoWithFrameCallback = HTMLVideoElement & {
  requestVideoFrameCallback?: (
    callback: (now: number, metadata: CameraFrameMetadata) => void,
  ) => number;
  cancelVideoFrameCallback?: (handle: number) => void;
};

const delay = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

const roundMs = (value: number): number => Math.round(value);

const copyPlainRecord = (value: unknown): Record<string, unknown> | null => {
  if (!value || typeof value !== 'object') return null;
  const out: Record<string, unknown> = {};
  for (const [key, raw] of Object.entries(value as Record<string, unknown>)) {
    if (raw === undefined || typeof raw === 'function') continue;
    out[key] = raw;
  }
  return Object.keys(out).length > 0 ? out : null;
};

const mediaPrimarySrc = (item: MediaItem): string | null =>
  'src' in item ? item.src : item.leftSrc;

const mediaSecondarySrc = (item: MediaItem): string | null =>
  'rightSrc' in item ? item.rightSrc : null;

const pickRecordingMimeType = (): string | undefined => {
  if (typeof MediaRecorder === 'undefined') return undefined;
  const candidates = [
    'video/webm;codecs=vp9,opus',
    'video/webm;codecs=vp8,opus',
    'video/webm',
    'video/mp4;codecs=h264,aac',
    'video/mp4',
  ];
  for (const candidate of candidates) {
    try {
      if (typeof MediaRecorder.isTypeSupported === 'function' && MediaRecorder.isTypeSupported(candidate)) {
        return candidate;
      }
    } catch {
      // ignore
    }
  }
  return undefined;
};

const logDebug = (message: string, payload?: unknown) => {
  if (!DEBUG_ENABLED) return;
  if (payload !== undefined) {
    console.debug(message, payload);
  } else {
    console.debug(message);
  }
};

export default function Gallery() {
  const location = useLocation();
  const nav = useNavigate();
  const workflow = useMemo(() => resolveArgusWorkflow(location.search), [location.search]);
  const videoCollectionMode = isVideoWorkflow(workflow.runnerMode);
  const qcEnabled = videoCollectionMode && workflow.workflowId === 'screening-v2';
  const shouldPrimeStimuli = workflow.workflowId === 'screening-v2';

  useEffect(() => {
    persistArgusWorkflow(workflow);
  }, [workflow]);
  const {
    stream,
    hasPermission,
    faceOK,
    setFaceOK,
    selectedFaceAnchor,
    setSelectedFaceAnchor,
    addSample,
    samples,
    setVideoArtifact,
    setCaptureQc,
    setCaptureTelemetry,
    startTimelineIfNeeded,
    startSegment,
    endActiveSegment,
  } = useSession();
  const [items, setItems] = useState<MediaItem[] | null>(null);
  const [idx, setIdx] = useState(0);
  const [err, setErr] = useState<string | null>(null);
  const initialFormDataId = (() => {
    try {
      const raw = localStorage.getItem('argus_formDataId');
      if (!raw) return null;
      const parsed = Number(raw);
      return Number.isFinite(parsed) && parsed > 0 ? Math.trunc(parsed) : null;
    } catch {
      return null;
    }
  })();
  const [formDataId, setFormDataId] = useState<number | null>(initialFormDataId);
  const [finishing, setFinishing] = useState(false);
  const [capturedCount, setCapturedCount] = useState(0);
  const [faceLossMs, setFaceLossMs] = useState(0);
  const idxRef = useRef(0);

  const mediaImg = useRef<HTMLImageElement>(null);
  const mediaVid = useRef<HTMLVideoElement>(null);
  const mediaPair = useRef<HTMLDivElement>(null);
  const mediaPairLeft = useRef<HTMLImageElement>(null);
  const mediaPairRight = useRef<HTMLImageElement>(null);
  const cam = useRef<HTMLVideoElement>(null);
  const landmarkerRef = useRef<FaceLandmarker>();
  const workerRef = useRef<Worker | null>(null);
  const dbRef = useRef<IDBPDatabase<MyDB> | null>(null);
  const formIdRef = useRef<number | null>(initialFormDataId);
  const queueRef = useRef<PendingRecord[]>([]);
  const flushPromiseRef = useRef<Promise<void> | null>(null);
  const anchorRef = useRef<FaceAnchor | null>(selectedFaceAnchor);
  const faceOkRef = useRef(faceOK);
  const capturedCountRef = useRef(0);
  const lastHudUpdateRef = useRef(0);
  const detectErrorCountRef = useRef(0);
  const didAutoFallbackRef = useRef(false);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const recorderChunksRef = useRef<Blob[]>([]);
  const recorderMimeRef = useRef<string | null>(null);
  const faceLossBucketRef = useRef(0);
  const telemetryRef = useRef<ArgusCaptureTelemetryV1 | null>(null);
  const activeStimulusRef = useRef<ArgusStimulusTimingV1 | null>(null);
  const expectedStimulusOnsetRef = useRef(0);
  const frameSeqRef = useRef(0);
  const lastFrameRelativeMsRef = useRef<number | null>(null);
  const lastPresentedFramesRef = useRef<number | null>(null);
  const droppedFrameEstimateRef = useRef(0);
  const maxFrameGapMsRef = useRef(0);
  const maxRafGapMsRef = useRef(0);
  const lastCameraSignatureRef = useRef('');
  const qcRef = useRef<QcAccumulator>({
    totalFrames: 0,
    facePresentFrames: 0,
    multiFaceFrames: 0,
    longestNoFaceMs: 0,
    currentNoFaceStartMs: null,
    segments: new Map(),
  });

  useEffect(() => {
    idxRef.current = idx;
  }, [idx]);

  useEffect(() => {
    anchorRef.current = selectedFaceAnchor;
  }, [selectedFaceAnchor]);

  useEffect(() => {
    faceOkRef.current = faceOK;
  }, [faceOK]);

  useEffect(() => {
    const root = document.documentElement;
    const body = document.body;
    if (!root || !body) return;
    root.classList.add('hide-cursor');
    body.classList.add('hide-cursor');
    return () => {
      root.classList.remove('hide-cursor');
      body.classList.remove('hide-cursor');
    };
  }, []);

  useEffect(() => {
    if (!hasPermission) nav('/');
  }, [hasPermission, nav]);

  useEffect(() => {
    let mounted = true;
    const loadConfig = shouldPrimeStimuli
      ? prepareGalleryStimuli(workflow.configPath)
      : loadGalleryConfig(workflow.configPath);
    loadConfig
      .then((cfg) => mounted && setItems(cfg.media))
      .catch((e) => mounted && setErr(String(e)));
    getFaceLandmarker().then((lm) => {
      if (mounted) landmarkerRef.current = lm;
    });
    return () => {
      mounted = false;
    };
  }, [shouldPrimeStimuli, workflow.configPath]);

  useEffect(() => {
    setVideoArtifact(null);
    setCaptureQc(null);
    setCaptureTelemetry(null);
    setFaceLossMs(0);
    faceLossBucketRef.current = 0;
    telemetryRef.current = null;
    activeStimulusRef.current = null;
    expectedStimulusOnsetRef.current = 0;
    frameSeqRef.current = 0;
    lastFrameRelativeMsRef.current = null;
    lastPresentedFramesRef.current = null;
    droppedFrameEstimateRef.current = 0;
    maxFrameGapMsRef.current = 0;
    maxRafGapMsRef.current = 0;
    lastCameraSignatureRef.current = '';
    qcRef.current = {
      totalFrames: 0,
      facePresentFrames: 0,
      multiFaceFrames: 0,
      longestNoFaceMs: 0,
      currentNoFaceStartMs: null,
      segments: new Map(),
    };
    if (videoCollectionMode) {
      formIdRef.current = null;
      setFormDataId(null);
      try {
        localStorage.removeItem('argus_formDataId');
      } catch (error) {
        logDebug('[Gallery] failed to clear stale argus_formDataId', error);
      }
    }
  }, [setCaptureQc, setCaptureTelemetry, setVideoArtifact, videoCollectionMode]);

  const finalizeRecording = useCallback((): Blob | null => {
    const chunks = recorderChunksRef.current;
    const mimeType = recorderMimeRef.current || chunks[0]?.type || 'video/webm';
    const blob = chunks.length > 0 ? new Blob(chunks, { type: mimeType }) : null;
    const nowMs = performance.now();
    const telemetry = telemetryRef.current;
    if (videoCollectionMode && telemetry) {
      telemetry.runtimeEvents.push({
        type: 'recorder_stop',
        relativeMs: roundMs(nowMs - telemetry.clock.sessionStartPerformanceMs),
        performanceMs: roundMs(nowMs),
        wallTime: new Date(Math.round(performance.timeOrigin + nowMs)).toISOString(),
        mediaIndex: idxRef.current,
        visibilityState: typeof document !== 'undefined' ? String(document.visibilityState) : null,
        hidden: typeof document !== 'undefined' ? document.hidden : undefined,
        detail: {
          chunkCount: chunks.length,
          mimeType,
          videoBytes: blob?.size ?? 0,
        },
      });
    }
    setVideoArtifact(blob, mimeType);
    if (blob && blob.size > 0) {
      void saveVideoArtifact(getOrCreateClientRunId(), blob, mimeType).catch((error) =>
        logDebug('[Gallery] failed to persist video artifact', error),
      );
    }
    recorderChunksRef.current = [];
    recorderMimeRef.current = blob ? mimeType : null;
    return blob;
  }, [setVideoArtifact, videoCollectionMode]);

  const stopRecording = useCallback(async (): Promise<Blob | null> => {
    const recorder = recorderRef.current;
    if (!recorder) return finalizeRecording();
    if (recorder.state === 'inactive') {
      recorderRef.current = null;
      return finalizeRecording();
    }
    return new Promise<Blob | null>((resolve) => {
      let done = false;
      let timeoutId: number | null = null;

      const cleanup = () => {
        recorder.removeEventListener('stop', onStop);
        recorder.removeEventListener('error', onError);
        if (timeoutId !== null) window.clearTimeout(timeoutId);
      };
      const finish = () => {
        if (done) return;
        done = true;
        cleanup();
        recorderRef.current = null;
        resolve(finalizeRecording());
      };
      const onStop = () => {
        // Some browsers deliver the last dataavailable event very close to stop.
        window.setTimeout(finish, 150);
      };
      const onError = () => finish();

      recorder.addEventListener('stop', onStop);
      recorder.addEventListener('error', onError);
      timeoutId = window.setTimeout(finish, RECORDER_STOP_TIMEOUT_MS);

      try {
        if (recorder.state === 'recording' && typeof recorder.requestData === 'function') {
          recorder.requestData();
        }
      } catch (error) {
        logDebug('[Gallery] failed to request final recorder data', error);
      }

      try {
        recorder.stop();
      } catch (error) {
        logDebug('[Gallery] failed to stop recorder', error);
        finish();
      }
    });
  }, [finalizeRecording]);

  const ensureTelemetry = useCallback((nowMs = performance.now()): ArgusCaptureTelemetryV1 => {
    let telemetry = telemetryRef.current;
    if (!telemetry) {
      telemetry = {
        version: 1,
        clock: {
          source: 'performance.now',
          timeOriginUnixMs: Math.round(performance.timeOrigin),
          sessionStartPerformanceMs: nowMs,
          sessionStartUnixMs: Math.round(performance.timeOrigin + nowMs),
        },
        camera: {
          frameCount: 0,
          droppedFrameEstimate: 0,
          firstFrameRelativeMs: null,
          lastFrameRelativeMs: null,
        },
        stimulusTimings: [],
        frameTimestamps: [],
        runtimeEvents: [],
        summary: {
          visibilityEventCount: 0,
          throttleEventCount: 0,
          maxFrameGapMs: null,
          maxRafGapMs: null,
          droppedFrameEstimate: 0,
        },
      };
      telemetryRef.current = telemetry;
    }
    return telemetry;
  }, []);

  const relativeTelemetryMs = useCallback(
    (nowMs: number): number => roundMs(nowMs - ensureTelemetry(nowMs).clock.sessionStartPerformanceMs),
    [ensureTelemetry],
  );

  const appendRuntimeEvent = useCallback(
    (
      type: ArgusRuntimeTelemetryEventV1['type'],
      nowMs = performance.now(),
      detail?: Record<string, unknown>,
    ) => {
      if (!videoCollectionMode) return;
      const telemetry = ensureTelemetry(nowMs);
      const relativeMs = relativeTelemetryMs(nowMs);
      telemetry.runtimeEvents.push({
        type,
        relativeMs,
        performanceMs: roundMs(nowMs),
        wallTime: new Date(Math.round(performance.timeOrigin + nowMs)).toISOString(),
        mediaIndex: idxRef.current,
        visibilityState:
          typeof document !== 'undefined' ? String(document.visibilityState) : null,
        hidden: typeof document !== 'undefined' ? document.hidden : undefined,
        gapMs:
          typeof detail?.['gapMs'] === 'number' ? roundMs(Number(detail['gapMs'])) : undefined,
        detail,
      });
    },
    [ensureTelemetry, relativeTelemetryMs, videoCollectionMode],
  );

  const updateCameraTelemetry = useCallback(
    (nowMs = performance.now()) => {
      if (!videoCollectionMode) return;
      const telemetry = ensureTelemetry(nowMs);
      const track = stream?.getVideoTracks?.()[0] ?? null;
      const settings = track?.getSettings?.() ?? null;
      const constraints = track?.getConstraints?.() ?? null;
      const video = cam.current;
      const trackSettings = copyPlainRecord(settings);
      const trackConstraints = copyPlainRecord(constraints);
      const settingsWidth =
        settings && typeof settings.width === 'number' ? settings.width : undefined;
      const settingsHeight =
        settings && typeof settings.height === 'number' ? settings.height : undefined;
      const settingsFrameRate =
        settings && typeof settings.frameRate === 'number' && Number.isFinite(settings.frameRate)
          ? settings.frameRate
          : null;

      telemetry.camera.trackSettings = trackSettings;
      telemetry.camera.trackConstraints = trackConstraints;
      telemetry.camera.videoWidth = video?.videoWidth || settingsWidth || null;
      telemetry.camera.videoHeight = video?.videoHeight || settingsHeight || null;
      telemetry.camera.requestedFrameRate = settingsFrameRate;

      const signature = JSON.stringify({
        trackSettings,
        trackConstraints,
        videoWidth: telemetry.camera.videoWidth,
        videoHeight: telemetry.camera.videoHeight,
      });
      if (signature !== lastCameraSignatureRef.current) {
        lastCameraSignatureRef.current = signature;
        appendRuntimeEvent('camera_settings', nowMs, {
          trackSettings,
          trackConstraints,
          videoWidth: telemetry.camera.videoWidth,
          videoHeight: telemetry.camera.videoHeight,
        });
      }
    },
    [appendRuntimeEvent, ensureTelemetry, stream, videoCollectionMode],
  );

  const recordStimulusOnset = useCallback(
    (mediaIndex: number, item: MediaItem, nowMs: number, mediaDurationMs?: number | null) => {
      if (!videoCollectionMode) return;
      const telemetry = ensureTelemetry(nowMs);
      const actualOnsetMs = relativeTelemetryMs(nowMs);
      const rawDuration = Number(item.duration);
      const plannedDurationMs =
        Number.isFinite(rawDuration) && rawDuration >= 0
          ? Math.round(rawDuration)
          : mediaDurationMs != null && Number.isFinite(mediaDurationMs)
            ? Math.round(mediaDurationMs)
            : null;
      const requestedOnsetMs = roundMs(expectedStimulusOnsetRef.current);
      const timing: ArgusStimulusTimingV1 = {
        mediaIndex,
        type: item.type,
        primarySrc: mediaPrimarySrc(item),
        secondarySrc: mediaSecondarySrc(item),
        skipCapture: item.skipCapture === true,
        plannedDurationMs,
        mediaDurationMs:
          mediaDurationMs != null && Number.isFinite(mediaDurationMs)
            ? Math.round(mediaDurationMs)
            : null,
        requestedOnsetMs,
        actualOnsetMs,
        onsetLagMs: roundMs(actualOnsetMs - requestedOnsetMs),
      };
      telemetry.stimulusTimings.push(timing);
      activeStimulusRef.current = timing;
    },
    [ensureTelemetry, relativeTelemetryMs, videoCollectionMode],
  );

  const recordStimulusOffset = useCallback(
    (mediaIndex: number, nowMs: number) => {
      if (!videoCollectionMode) return;
      const actualOffsetMs = relativeTelemetryMs(nowMs);
      const active = activeStimulusRef.current;
      const telemetry = ensureTelemetry(nowMs);
      const timing =
        active?.mediaIndex === mediaIndex
          ? active
          : [...telemetry.stimulusTimings].reverse().find((item) => item.mediaIndex === mediaIndex);
      if (!timing || timing.actualOffsetMs != null) return;
      const plannedDurationMs = timing.plannedDurationMs ?? timing.mediaDurationMs ?? null;
      const requestedOffsetMs =
        plannedDurationMs != null ? roundMs(timing.requestedOnsetMs + plannedDurationMs) : null;
      timing.requestedOffsetMs = requestedOffsetMs;
      timing.actualOffsetMs = actualOffsetMs;
      timing.offsetLagMs =
        requestedOffsetMs != null ? roundMs(actualOffsetMs - requestedOffsetMs) : null;
      timing.actualDurationMs = roundMs(actualOffsetMs - timing.actualOnsetMs);
      expectedStimulusOnsetRef.current = requestedOffsetMs ?? actualOffsetMs;
      if (activeStimulusRef.current === timing) activeStimulusRef.current = null;
    },
    [ensureTelemetry, relativeTelemetryMs, videoCollectionMode],
  );

  const recordCameraFrame = useCallback(
    (nowMs: number, metadata?: CameraFrameMetadata) => {
      if (!videoCollectionMode) return;
      const telemetry = ensureTelemetry(nowMs);
      const relativeMs = relativeTelemetryMs(nowMs);
      const seq = frameSeqRef.current + 1;
      frameSeqRef.current = seq;

      const lastRelativeMs = lastFrameRelativeMsRef.current;
      const gapMs = lastRelativeMs == null ? 0 : relativeMs - lastRelativeMs;
      if (lastRelativeMs != null && gapMs > FRAME_GAP_THRESHOLD_MS) {
        maxFrameGapMsRef.current = Math.max(maxFrameGapMsRef.current, gapMs);
        const frameRate =
          typeof telemetry.camera.requestedFrameRate === 'number' &&
          telemetry.camera.requestedFrameRate > 0
            ? telemetry.camera.requestedFrameRate
            : 30;
        const expectedMs = 1000 / frameRate;
        const estimatedDrops = Math.max(0, Math.round(gapMs / expectedMs) - 1);
        droppedFrameEstimateRef.current += estimatedDrops;
        telemetry.runtimeEvents.push({
          type: 'frame_gap',
          relativeMs,
          performanceMs: roundMs(nowMs),
          wallTime: new Date(Math.round(performance.timeOrigin + nowMs)).toISOString(),
          mediaIndex: idxRef.current,
          visibilityState:
            typeof document !== 'undefined' ? String(document.visibilityState) : null,
          hidden: typeof document !== 'undefined' ? document.hidden : undefined,
          gapMs: roundMs(gapMs),
          detail: { estimatedDroppedFrames: estimatedDrops },
        });
      }
      lastFrameRelativeMsRef.current = relativeMs;

      if (typeof metadata?.presentedFrames === 'number') {
        const lastPresented = lastPresentedFramesRef.current;
        if (lastPresented != null && metadata.presentedFrames > lastPresented + 1) {
          droppedFrameEstimateRef.current += metadata.presentedFrames - lastPresented - 1;
        }
        lastPresentedFramesRef.current = metadata.presentedFrames;
      }

      telemetry.frameTimestamps.push({
        seq,
        relativeMs,
        performanceMs: roundMs(nowMs),
        mediaIndex: items && idxRef.current < items.length ? idxRef.current : null,
        mediaTimeMs:
          typeof metadata?.mediaTime === 'number' ? roundMs(metadata.mediaTime * 1000) : null,
        presentedFrames:
          typeof metadata?.presentedFrames === 'number' ? metadata.presentedFrames : null,
        expectedDisplayRelativeMs:
          typeof metadata?.expectedDisplayTime === 'number'
            ? relativeTelemetryMs(metadata.expectedDisplayTime)
            : null,
        width:
          typeof metadata?.width === 'number'
            ? metadata.width
            : cam.current?.videoWidth || telemetry.camera.videoWidth || null,
        height:
          typeof metadata?.height === 'number'
            ? metadata.height
            : cam.current?.videoHeight || telemetry.camera.videoHeight || null,
      });
      telemetry.camera.frameCount = telemetry.frameTimestamps.length;
      telemetry.camera.firstFrameRelativeMs = telemetry.frameTimestamps[0]?.relativeMs ?? null;
      telemetry.camera.lastFrameRelativeMs = relativeMs;
      telemetry.camera.droppedFrameEstimate = droppedFrameEstimateRef.current;
    },
    [ensureTelemetry, items, relativeTelemetryMs, videoCollectionMode],
  );

  const buildCaptureTelemetry = useCallback((): ArgusCaptureTelemetryV1 | null => {
    const telemetry = telemetryRef.current;
    if (!telemetry) return null;
    updateCameraTelemetry(performance.now());
    const frames = telemetry.frameTimestamps;
    const firstFrameRelativeMs = frames[0]?.relativeMs ?? null;
    const lastFrameRelativeMs = frames[frames.length - 1]?.relativeMs ?? null;
    const actualFps =
      frames.length > 1 &&
      firstFrameRelativeMs != null &&
      lastFrameRelativeMs != null &&
      lastFrameRelativeMs > firstFrameRelativeMs
        ? ((frames.length - 1) * 1000) / (lastFrameRelativeMs - firstFrameRelativeMs)
        : null;
    const visibilityEventCount = telemetry.runtimeEvents.filter((event) =>
      ['visibilitychange', 'pagehide', 'pageshow', 'blur', 'focus'].includes(event.type),
    ).length;
    const throttleEventCount = telemetry.runtimeEvents.filter((event) =>
      ['raf_gap', 'frame_gap'].includes(event.type),
    ).length;
    telemetry.camera.actualFps = actualFps != null ? Number(actualFps.toFixed(2)) : null;
    telemetry.camera.frameCount = frames.length;
    telemetry.camera.firstFrameRelativeMs = firstFrameRelativeMs;
    telemetry.camera.lastFrameRelativeMs = lastFrameRelativeMs;
    telemetry.camera.droppedFrameEstimate = droppedFrameEstimateRef.current;
    telemetry.summary = {
      visibilityEventCount,
      throttleEventCount,
      maxFrameGapMs: maxFrameGapMsRef.current > 0 ? roundMs(maxFrameGapMsRef.current) : null,
      maxRafGapMs: maxRafGapMsRef.current > 0 ? roundMs(maxRafGapMsRef.current) : null,
      droppedFrameEstimate: droppedFrameEstimateRef.current,
    };
    telemetry.finalizedAtClient = new Date().toISOString();
    return {
      ...telemetry,
      camera: { ...telemetry.camera },
      clock: { ...telemetry.clock },
      stimulusTimings: telemetry.stimulusTimings.map((timing) => ({ ...timing })),
      frameTimestamps: telemetry.frameTimestamps.map((frame) => ({ ...frame })),
      runtimeEvents: telemetry.runtimeEvents.map((event) => ({ ...event })),
      summary: { ...telemetry.summary },
    };
  }, [updateCameraTelemetry]);

  const recordCaptureQcFrame = useCallback(
    (mediaIndex: number, faceCount: number, nowMs: number) => {
      if (!qcEnabled) return;

      const qc = qcRef.current;
      qc.totalFrames += 1;
      if (faceCount > 0) qc.facePresentFrames += 1;
      if (faceCount > 1) qc.multiFaceFrames += 1;

      const segment = qc.segments.get(mediaIndex) ?? {
        mediaIndex,
        totalFrames: 0,
        facePresentFrames: 0,
      };
      segment.totalFrames += 1;
      if (faceCount > 0) segment.facePresentFrames += 1;
      qc.segments.set(mediaIndex, segment);

      if (faceCount > 0) {
        if (qc.currentNoFaceStartMs !== null) {
          qc.longestNoFaceMs = Math.max(qc.longestNoFaceMs, nowMs - qc.currentNoFaceStartMs);
        }
        qc.currentNoFaceStartMs = null;
        if (faceLossBucketRef.current !== 0) {
          faceLossBucketRef.current = 0;
          setFaceLossMs(0);
        }
        return;
      }

      if (qc.currentNoFaceStartMs === null) qc.currentNoFaceStartMs = nowMs;
      const missingMs = nowMs - qc.currentNoFaceStartMs;
      qc.longestNoFaceMs = Math.max(qc.longestNoFaceMs, missingMs);

      const nextBucket =
        missingMs >= QC_FACE_LOST_OVERLAY_MS ? Math.max(1, Math.floor(missingMs / 500)) : 0;
      if (nextBucket !== faceLossBucketRef.current) {
        faceLossBucketRef.current = nextBucket;
        setFaceLossMs(nextBucket > 0 ? Math.round(missingMs) : 0);
      }

    },
    [qcEnabled],
  );

  const buildCaptureQc = useCallback((): ArgusCaptureQcV1 => {
    const qc = qcRef.current;
    if (qc.currentNoFaceStartMs !== null) {
      qc.longestNoFaceMs = Math.max(qc.longestNoFaceMs, performance.now() - qc.currentNoFaceStartMs);
    }

    const segments: ArgusCaptureQcSegment[] = Array.from(qc.segments.values())
      .sort((a, b) => a.mediaIndex - b.mediaIndex)
      .map((segment) => ({
        mediaIndex: segment.mediaIndex,
        totalFrames: segment.totalFrames,
        facePresentFrames: segment.facePresentFrames,
        coverage: segment.totalFrames > 0 ? segment.facePresentFrames / segment.totalFrames : 0,
      }));
    const failedSegmentCount = segments.filter(
      (segment) => segment.coverage < QC_MIN_SEGMENT_FACE_RATIO,
    ).length;
    const segmentCount = segments.length;
    const facePresentRatio =
      qc.totalFrames > 0 ? qc.facePresentFrames / qc.totalFrames : 0;
    let reason: string | null = null;
    if (qc.totalFrames <= 0) {
      reason = 'no_qc_frames';
    } else if (facePresentRatio < QC_MIN_FACE_PRESENT_RATIO) {
      reason = 'insufficient_face_coverage';
    }

    return {
      version: 1,
      passed: reason === null,
      reason,
      totalFrames: qc.totalFrames,
      facePresentFrames: qc.facePresentFrames,
      facePresentRatio,
      multiFaceFrames: qc.multiFaceFrames,
      longestNoFaceMs: Math.round(qc.longestNoFaceMs),
      minFacePresentRatio: QC_MIN_FACE_PRESENT_RATIO,
      maxAllowedNoFaceMs: QC_MAX_NO_FACE_MS,
      minSegmentFaceRatio: QC_MIN_SEGMENT_FACE_RATIO,
      maxFailedSegmentRatio: QC_MAX_FAILED_SEGMENT_RATIO,
      failedSegmentCount,
      segmentCount,
      segments,
    };
  }, []);

  const setCamRef = useCallback(
    (el: HTMLVideoElement | null) => {
      cam.current = el;
      if (el && stream) {
        if (el.srcObject !== stream) {
          el.srcObject = stream;
        }
        el.play().catch((error) => logDebug('[Gallery] autoplay attach failed', error));
      }
    },
    [stream],
  );

  useEffect(() => {
    const el = cam.current;
    if (!stream || !el) return;
    if (el.srcObject !== stream) el.srcObject = stream;
    if (el.paused) {
      el.play().catch((error) => logDebug('[Gallery] autoplay resume failed', error));
    }
  }, [stream]);

  useEffect(() => {
    if (!videoCollectionMode) return;
    ensureTelemetry(performance.now());

    const recordVisibilityEvent = (type: ArgusRuntimeTelemetryEventV1['type']) => {
      appendRuntimeEvent(type, performance.now(), {
        visibilityState: document.visibilityState,
        hidden: document.hidden,
        hasFocus: document.hasFocus(),
      });
    };

    const onVisibilityChange = () => recordVisibilityEvent('visibilitychange');
    const onPageHide = () => recordVisibilityEvent('pagehide');
    const onPageShow = () => recordVisibilityEvent('pageshow');
    const onBlur = () => recordVisibilityEvent('blur');
    const onFocus = () => recordVisibilityEvent('focus');

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pagehide', onPageHide);
    window.addEventListener('pageshow', onPageShow);
    window.addEventListener('blur', onBlur);
    window.addEventListener('focus', onFocus);

    let rafId = 0;
    let lastRaf = performance.now();
    const tick = (nowMs: number) => {
      const gapMs = nowMs - lastRaf;
      if (gapMs > RAF_GAP_THRESHOLD_MS) {
        maxRafGapMsRef.current = Math.max(maxRafGapMsRef.current, gapMs);
        appendRuntimeEvent('raf_gap', nowMs, { gapMs: roundMs(gapMs) });
      }
      lastRaf = nowMs;
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('pagehide', onPageHide);
      window.removeEventListener('pageshow', onPageShow);
      window.removeEventListener('blur', onBlur);
      window.removeEventListener('focus', onFocus);
      cancelAnimationFrame(rafId);
    };
  }, [appendRuntimeEvent, ensureTelemetry, videoCollectionMode]);

  useEffect(() => {
    if (!videoCollectionMode) return;
    if (!stream) return;
    const video = cam.current as VideoWithFrameCallback | null;
    if (!video) return;

    let cancelled = false;
    let callbackHandle: number | null = null;
    let rafId: number | null = null;
    let started = false;
    let lastMediaTime = -1;

    const stopLoop = () => {
      if (callbackHandle !== null && typeof video.cancelVideoFrameCallback === 'function') {
        video.cancelVideoFrameCallback(callbackHandle);
      }
      if (rafId !== null) cancelAnimationFrame(rafId);
      callbackHandle = null;
      rafId = null;
    };

    const startLoop = () => {
      if (cancelled || started) return;
      started = true;
      updateCameraTelemetry(performance.now());
      if (typeof video.requestVideoFrameCallback === 'function') {
        const onVideoFrame = (nowMs: number, metadata: CameraFrameMetadata) => {
          if (cancelled) return;
          recordCameraFrame(nowMs, metadata);
          callbackHandle = video.requestVideoFrameCallback?.(onVideoFrame) ?? null;
        };
        callbackHandle = video.requestVideoFrameCallback(onVideoFrame);
        return;
      }

      const fallbackTick = (nowMs: number) => {
        if (cancelled) return;
        if (video.readyState >= 2 && video.currentTime !== lastMediaTime) {
          lastMediaTime = video.currentTime;
          recordCameraFrame(nowMs, {
            mediaTime: video.currentTime,
            width: video.videoWidth,
            height: video.videoHeight,
          });
        }
        rafId = requestAnimationFrame(fallbackTick);
      };
      rafId = requestAnimationFrame(fallbackTick);
    };

    const onMetadata = () => {
      updateCameraTelemetry(performance.now());
      startLoop();
    };
    video.addEventListener('loadedmetadata', onMetadata);
    video.addEventListener('resize', onMetadata);
    if (video.readyState >= 1) startLoop();

    return () => {
      cancelled = true;
      video.removeEventListener('loadedmetadata', onMetadata);
      video.removeEventListener('resize', onMetadata);
      stopLoop();
    };
  }, [recordCameraFrame, stream, updateCameraTelemetry, videoCollectionMode]);

  useEffect(() => {
    if (!videoCollectionMode) return;
    if (!stream) return;
    if (!items || items.length === 0) return;
    if (typeof MediaRecorder === 'undefined') {
      setErr('当前浏览器不支持完整视频采集，请更换为较新的 Chrome、Edge 或 Safari。');
      return;
    }
    if (recorderRef.current) return;

    try {
      const mimeType = pickRecordingMimeType();
      const preferredVideoBitsPerSecond =
        typeof workflow.videoBitsPerSecond === 'number' && workflow.videoBitsPerSecond > 0
          ? workflow.videoBitsPerSecond
          : 850_000;
      const options = mimeType
        ? { mimeType, videoBitsPerSecond: preferredVideoBitsPerSecond }
        : { videoBitsPerSecond: preferredVideoBitsPerSecond };
      const recorder = new MediaRecorder(stream, options);
      recorderChunksRef.current = [];
      recorderMimeRef.current = mimeType ?? recorder.mimeType ?? null;
      recorder.addEventListener('dataavailable', (event) => {
        if (event.data && event.data.size > 0) {
          recorderChunksRef.current.push(event.data);
        }
      });
      recorder.addEventListener('error', () => {
        setErr('视频录制失败，请重新开始采集。');
      });
      recorder.start(1000);
      appendRuntimeEvent('recorder_start', performance.now(), {
        mimeType: mimeType ?? recorder.mimeType ?? null,
        videoBitsPerSecond: preferredVideoBitsPerSecond,
      });
      recorderRef.current = recorder;
    } catch (error) {
      logDebug('[Gallery] failed to start video recording', error);
      setErr('无法启动完整视频录制，请检查浏览器权限后重试。');
    }

    return () => {
      const active = recorderRef.current;
      if (active && active.state !== 'inactive') {
        try {
          active.stop();
        } catch (error) {
          logDebug('[Gallery] failed to stop recorder during cleanup', error);
        }
      }
    };
  }, [
    appendRuntimeEvent,
    items,
    setErr,
    stream,
    videoCollectionMode,
    workflow.videoBitsPerSecond,
  ]);

  const writeChunkToIDB = useCallback(
    async (records: PendingRecord[], attempt = 0): Promise<void> => {
      if (!records.length) return;
      let db = dbRef.current;
      if (!db) {
        try {
          db = await openOrCreateDB();
          dbRef.current = db;
        } catch (error) {
          if (attempt + 1 >= MAX_WRITE_RETRIES) {
            throw error instanceof Error ? error : new Error(String(error));
          }
          await delay(WRITE_RETRY_DELAY_MS);
          return writeChunkToIDB(records, attempt + 1);
        }
      }
      if (!db) return;
      try {
        const tx = db.transaction('landmarks', 'readwrite');
        const store = tx.objectStore('landmarks');
        for (const record of records) {
          await store.put(record);
        }
        await tx.done;
        if (CAPTURE_DEBUG) {
          console.debug('[capture-debug] wrote records to IDB', {
            count: records.length,
            formDataId: records[0]?.formDataId,
          });
        }
      } catch (error) {
        const isNotFound = error instanceof DOMException && error.name === 'NotFoundError';
        if (isNotFound) {
          try {
            db.close();
          } catch (closeErr) {
            logDebug('[Gallery] failed to close IDB after NotFoundError', closeErr);
          }
          dbRef.current = null;
          try {
            if (typeof indexedDB !== 'undefined') {
              indexedDB.deleteDatabase(DB_NAME);
            }
          } catch (delErr) {
            logDebug('[Gallery] failed to delete IDB', delErr);
          }
        }
        if (attempt + 1 >= MAX_WRITE_RETRIES) {
          throw error instanceof Error ? error : new Error(String(error));
        }
        await delay(WRITE_RETRY_DELAY_MS);
        return writeChunkToIDB(records, attempt + 1);
      }
    },
    [],
  );

  const flushQueue = useCallback(
    (flushAll = false): Promise<void> => {
      const execute = async () => {
        do {
          const queue = queueRef.current;
          if (!queue.length) break;
          const count = flushAll ? queue.length : Math.min(queue.length, IDB_BATCH_SIZE);
          const chunk = queue.splice(0, count);
          if (!chunk.length) break;
          try {
            await writeChunkToIDB(chunk);
            if (CAPTURE_DEBUG) {
              console.debug('[capture-debug] flushed queue chunk', {
                chunkSize: chunk.length,
                remaining: queueRef.current.length,
              });
            }
          } catch (error) {
            queue.unshift(...chunk);
            throw error;
          }
        } while (flushAll);
      };

      const base = flushPromiseRef.current ?? Promise.resolve();
      const next = base.then(execute);
      flushPromiseRef.current = next.finally(() => {
        if (flushPromiseRef.current === next) {
          flushPromiseRef.current = null;
        }
      });
      return flushPromiseRef.current;
    },
    [writeChunkToIDB],
  );

  const stopWorker = useCallback(async (timeoutMs = WORKER_STOP_TIMEOUT_MS) => {
    const worker = workerRef.current;
    if (!worker) return;
    const donePromise = new Promise<void>((resolve) => {
      let done = false;
      const onMessage = (event: MessageEvent<WorkerMessage>) => {
        if (event.data?.type === 'done') {
          done = true;
          worker.removeEventListener('message', onMessage as EventListener);
          window.clearTimeout(timer);
          resolve();
        }
      };
      worker.addEventListener('message', onMessage as EventListener);
      const timer = window.setTimeout(() => {
        if (!done) {
          worker.removeEventListener('message', onMessage as EventListener);
          resolve();
        }
      }, timeoutMs);
    });
    try {
      worker.postMessage({ type: 'stop' });
    } catch (error) {
      logDebug('[Gallery] failed to post stop message', error);
    }
    await donePromise;
    try {
      worker.terminate();
    } catch (error) {
      logDebug('[Gallery] failed to terminate worker', error);
    }
    if (workerRef.current === worker) {
      workerRef.current = null;
    }
  }, []);

  const startWorker = useCallback(
    (fdId: number) => {
      if (workerRef.current) return;
      try {
        const worker = new Worker(new URL('../runner/uploadWorker.ts', import.meta.url), {
          type: 'module',
        });
        const handleMessage = (event: MessageEvent<WorkerMessage>) => {
          const eventType = event.data?.type;
          if (eventType === 'done') {
            worker.removeEventListener('message', handleMessage as EventListener);
            return;
          }
          if (eventType === 'auth-error') {
            worker.removeEventListener('message', handleMessage as EventListener);
            if (workerRef.current === worker) {
              workerRef.current = null;
            }
            try {
              worker.terminate();
            } catch (error) {
              logDebug('[Gallery] failed to terminate worker after auth error', error);
            }
            setErr('登录状态已过期，请重新登录。');
          }
        };
        worker.addEventListener('message', handleMessage as EventListener);
        workerRef.current = worker;
        worker.postMessage({
          type: 'start',
          formDataId: fdId,
          debug: CAPTURE_DEBUG,
          clientRunId: getOrCreateClientRunId(),
        });
      } catch (error) {
        logDebug('[Gallery] failed to start upload worker', error);
        setErr('无法启动数据上传，请刷新页面重试。');
      }
    },
    [setErr],
  );

  useEffect(() => {
    if (videoCollectionMode) {
      return () => {
        stopWorker().catch((error) => logDebug('[Gallery] stop worker on unmount failed', error));
      };
    }

    let cancelled = false;
    let intervalId: number | null = null;

    (async () => {
      try {
        const db = await openOrCreateDB();
        if (cancelled) {
          db.close();
          return;
        }
        dbRef.current = db;
        intervalId = window.setInterval(() => {
          flushQueue(false).catch((error) => logDebug('[Gallery] periodic flush error', error));
        }, FLUSH_INTERVAL_MS);
      } catch (error) {
        logDebug('[Gallery] failed to open IndexedDB', error);
      }
    })();

    return () => {
      cancelled = true;
      if (intervalId !== null) {
        window.clearInterval(intervalId);
      }
      flushQueue(true)
        .catch((error) => logDebug('[Gallery] flush on unmount failed', error))
        .then(() =>
          stopWorker().catch((error) => logDebug('[Gallery] stop worker on unmount failed', error)),
        )
        .finally(() => {
          try {
            dbRef.current?.close();
          } catch (error) {
            logDebug('[Gallery] failed to close IndexedDB', error);
          }
          dbRef.current = null;
        });
    };
  }, [flushQueue, stopWorker, videoCollectionMode]);

  useEffect(() => {
    if (videoCollectionMode) return;
    let active = true;

    (async () => {
      try {
        const existing = formIdRef.current;
        if (existing && existing > 0) {
          setFormDataId(existing);
          return;
        }
        // Try localStorage (Start page should populate this before we enter Gallery).
        let stored: number | null = null;
        try {
          const raw = localStorage.getItem('argus_formDataId');
          if (raw) {
            const parsed = Number(raw);
            stored = Number.isFinite(parsed) && parsed > 0 ? Math.trunc(parsed) : null;
          }
        } catch {
          stored = null;
        }
        if (stored && stored > 0) {
          formIdRef.current = stored;
          setFormDataId(stored);
          return;
        }
        const ident = `run-${Math.floor(Date.now() / 1000)}`;
        const { formDataId: fdId } = await submitForm(ident);
        if (!active) return;
        formIdRef.current = fdId;
        setFormDataId(fdId);
        try {
          localStorage.setItem('argus_formDataId', String(fdId));
        } catch (error) {
          logDebug('[Gallery] failed to persist formDataId', error);
        }
      } catch (error) {
        if (!active) return;
        logDebug('[Gallery] failed to create formDataId', error);
        setErr('无法创建测验运行，请稍后重试');
      }
    })();

    return () => {
      active = false;
    };
  }, [setErr, videoCollectionMode]);

  useEffect(() => {
    if (videoCollectionMode) return;
    if (!STREAM_UPLOAD_DURING_TEST) return;
    if (!formDataId || formDataId <= 0) return;
    if (finishing) return;
    startWorker(formDataId);
  }, [finishing, formDataId, startWorker, videoCollectionMode]);

  // Note: we intentionally do NOT clear records for the current formDataId here.
  // Deleting current-run keys can race with capture and cause silent data loss.

  useEffect(() => {
    let raf = 0;
    function loop() {
      const lm = landmarkerRef.current;
      const v = cam.current;
      if (lm && v && v.readyState >= 2 && items && items[idx] && items[idx].skipCapture !== true) {
        if (stream && !v.srcObject) {
          v.srcObject = stream;
        }
        if (v.paused) {
          v.play().catch((error) => logDebug('[Gallery] loop play resume failed', error));
        }
        const ts = performance.now();
        let res: ReturnType<FaceLandmarker['detectForVideo']> | null = null;
        let faceCountForQc: number | null = null;
        try {
          res = lm.detectForVideo(v, ts);
          detectErrorCountRef.current = 0;
        } catch (error) {
          faceCountForQc = 0;
          detectErrorCountRef.current += 1;
          logDebug('[Gallery] detectForVideo failed', error);
          if (faceOkRef.current) {
            faceOkRef.current = false;
            setFaceOK(false);
          }

          if (
            !didAutoFallbackRef.current &&
            getActiveFaceDelegate() === 'GPU' &&
            detectErrorCountRef.current >= 3
          ) {
            didAutoFallbackRef.current = true;
            forceFaceDelegate('CPU')
              .then((cpuLm) => {
                landmarkerRef.current = cpuLm;
              })
              .catch((e) => logDebug('[Gallery] CPU fallback failed', e));
          }
        }

        if (res) {
          const faces = res.faceLandmarks ?? [];
          const bounds = computeFaceBoundsList(
            faces as unknown as Array<Array<{ x: number; y: number }>>,
          );
          const count = bounds.length;
          faceCountForQc = count;

          let selectedIndex: number | null = null;
          const anchor = anchorRef.current;

          if (count === 0) {
            selectedIndex = null;
          } else if (count === 1) {
            selectedIndex = 0;
            if (!anchor) {
              const nextAnchor = { cx: bounds[0].cx, cy: bounds[0].cy };
              anchorRef.current = nextAnchor;
              setSelectedFaceAnchor(nextAnchor);
            }
          } else {
            if (anchor) {
              selectedIndex = pickFaceIndexByAnchor(bounds, anchor);
            } else {
              selectedIndex = pickLargestFaceIndex(bounds);
              const nextAnchor = { cx: bounds[selectedIndex].cx, cy: bounds[selectedIndex].cy };
              anchorRef.current = nextAnchor;
              setSelectedFaceAnchor(nextAnchor);
            }
          }

          const ok = selectedIndex !== null;
          if (faceOkRef.current !== ok) {
            faceOkRef.current = ok;
            setFaceOK(ok);
          }

          if (ok) {
            anchorRef.current = { cx: bounds[selectedIndex!].cx, cy: bounds[selectedIndex!].cy };
          }
          if (ok) {
            const pts = faces[selectedIndex!].map((p) => ({
              x: Number(p.x),
              y: Number(p.y),
              z: Number(p.z ?? 0),
            }));
            const lm468 = pts.slice(0, 468);
            const sample: EyeTraxSample = {
              timestamp: Math.round(ts),
              mediaIndex: idx,
              landmarks: lm468,
            };
            // Avoid storing the full landmark sequence in JS heap during long runs.
            // Chunked uploads use IndexedDB as the source of truth.
            if (!videoCollectionMode && !STREAM_UPLOAD_DURING_TEST) {
              addSample(sample);
            }
            capturedCountRef.current += 1;
            if (ts - lastHudUpdateRef.current >= 500) {
              lastHudUpdateRef.current = ts;
              setCapturedCount(capturedCountRef.current);
            }
            const fdId = formIdRef.current;
            if (!videoCollectionMode && fdId && fdId > 0) {
              const payload = {
                formDataId: fdId,
                mediaIndex: idx,
                landmarks: lm468,
                timestamp: Date.now(),
              } as PendingRecord;
              queueRef.current.push(payload);
              if (CAPTURE_DEBUG) {
                console.debug('[capture-debug] enqueue sample', {
                  mediaIndex: payload.mediaIndex,
                  queueSize: queueRef.current.length,
                });
              }
              if (queueRef.current.length >= IDB_BATCH_SIZE) {
                flushQueue(false).catch((error) => logDebug('[Gallery] flushQueue error', error));
              }
            }
          }
        }
        if (faceCountForQc !== null) {
          recordCaptureQcFrame(idx, faceCountForQc, ts);
        }
      }
      raf = requestAnimationFrame(loop);
    }
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [
    idx,
    items,
    addSample,
    stream,
    flushQueue,
    recordCaptureQcFrame,
    setFaceOK,
    setSelectedFaceAnchor,
    videoCollectionMode,
  ]);

  useEffect(() => {
    if (
      !items ||
      !mediaVid.current ||
      !mediaImg.current ||
      !mediaPair.current ||
      !mediaPairLeft.current ||
      !mediaPairRight.current
    ) {
      return;
    }
    const it = items[idx];
    const v = mediaVid.current;
    const img = mediaImg.current;
    const pair = mediaPair.current;
    const pairLeft = mediaPairLeft.current;
    const pairRight = mediaPairRight.current;
    let rafId: number | null = null;
    const now = performance.now();
    startTimelineIfNeeded(now);

    if (!it) return;

    const runTimedSegment = () => {
      const onsetMs = performance.now();
      recordStimulusOnset(idx, it, onsetMs);
      if (it.skipCapture !== true) {
        startSegment(idx, onsetMs);
      }
      const dur = Math.max(0, Number(it.duration) || 5000);
      const start = onsetMs;
      const tick = (nowTs: number) => {
        if (nowTs - start >= dur) {
          recordStimulusOffset(idx, nowTs);
          if (it.skipCapture !== true) endActiveSegment(nowTs);
          setIdx((i) => i + 1);
          return;
        }
        rafId = requestAnimationFrame(tick);
      };
      rafId = requestAnimationFrame(tick);
    };

    if (it.type === 'image') {
      let handled = false;
      const showImage = () => {
        if (handled) return;
        handled = true;
        v.classList.remove('show');
        pair.classList.remove('show');
        img.classList.add('show');
        runTimedSegment();
      };
      const handleImageError = () => {
        handled = true;
        logDebug('[Gallery] image failed to load', { src: it.src });
        appendRuntimeEvent('stimulus_error', performance.now(), {
          mediaIndex: idx,
          src: it.src,
          type: it.type,
        });
        setErr(`图片素材加载失败：${it.src}`);
      };

      img.onload = showImage;
      img.onerror = handleImageError;
      img.src = it.src;
      if (img.complete && img.naturalWidth > 0) {
        queueMicrotask(showImage);
      }
    } else if (it.type === 'pair') {
      let handled = false;
      let loaded = 0;
      const completeIfReady = () => {
        loaded += 1;
        if (handled || loaded < 2) return;
        handled = true;
        pairLeft.src = it.leftSrc;
        pairRight.src = it.rightSrc;
        img.classList.remove('show');
        v.classList.remove('show');
        pair.classList.add('show');
        runTimedSegment();
      };
      const handlePairError = (src: string) => {
        if (handled) return;
        handled = true;
        logDebug('[Gallery] pair image failed to load', { src });
        appendRuntimeEvent('stimulus_error', performance.now(), {
          mediaIndex: idx,
          src,
          type: it.type,
        });
        setErr(`图片素材加载失败：${src}`);
      };

      const leftPreload = new Image();
      const rightPreload = new Image();
      leftPreload.onload = completeIfReady;
      rightPreload.onload = completeIfReady;
      leftPreload.onerror = () => handlePairError(it.leftSrc);
      rightPreload.onerror = () => handlePairError(it.rightSrc);
      leftPreload.src = it.leftSrc;
      rightPreload.src = it.rightSrc;
      if (leftPreload.complete && leftPreload.naturalWidth > 0) {
        queueMicrotask(completeIfReady);
      }
      if (rightPreload.complete && rightPreload.naturalWidth > 0) {
        queueMicrotask(completeIfReady);
      }
    } else if (it.type === 'video') {
      let handled = false;
      const onCanPlay = async () => {
        if (handled) return;
        handled = true;
        img.classList.remove('show');
        pair.classList.remove('show');
        v.classList.add('show');
        try {
          await v.play();
          const onsetMs = performance.now();
          const mediaDurationMs =
            Number.isFinite(v.duration) && v.duration > 0 ? Math.round(v.duration * 1000) : null;
          recordStimulusOnset(idx, it, onsetMs, mediaDurationMs);
          if (it.skipCapture !== true) startSegment(idx, onsetMs);
        } catch (error) {
          logDebug('[Gallery] video autoplay failed', error);
          appendRuntimeEvent('stimulus_error', performance.now(), {
            mediaIndex: idx,
            src: it.src,
            type: it.type,
            error: error instanceof Error ? error.message : String(error),
          });
          setErr(`视频素材无法播放：${it.src}`);
        }
      };
      const onVideoError = () => {
        handled = true;
        logDebug('[Gallery] video failed to load', { src: it.src });
        appendRuntimeEvent('stimulus_error', performance.now(), {
          mediaIndex: idx,
          src: it.src,
          type: it.type,
        });
        setErr(`视频素材加载失败：${it.src}`);
      };
      v.muted = true;
      v.playsInline = true;
      (v as HTMLVideoElement & { webkitPlaysInline?: boolean }).webkitPlaysInline = true;
      v.autoplay = true;
      v.preload = 'auto';
      const onEnded = () => {
        const endMs = performance.now();
        recordStimulusOffset(idx, endMs);
        if (it.skipCapture !== true) endActiveSegment(endMs);
        setIdx((i) => i + 1);
      };

      v.addEventListener('loadeddata', onCanPlay);
      v.addEventListener('canplay', onCanPlay);
      v.addEventListener('error', onVideoError);
      v.addEventListener('ended', onEnded);
      v.src = it.src;
      v.load();
      if (v.readyState >= 2) {
        queueMicrotask(() => {
          void onCanPlay();
        });
      }
      return () => {
        v.pause();
        v.removeEventListener('loadeddata', onCanPlay);
        v.removeEventListener('canplay', onCanPlay);
        v.removeEventListener('error', onVideoError);
        v.removeEventListener('ended', onEnded);
        recordStimulusOffset(idx, performance.now());
        if (rafId) cancelAnimationFrame(rafId);
      };
    }

    return () => {
      img.onload = null;
      img.onerror = null;
      if (rafId) cancelAnimationFrame(rafId);
      recordStimulusOffset(idx, performance.now());
      if (it && it.skipCapture !== true) {
        endActiveSegment(performance.now());
      }
    };
  }, [
    appendRuntimeEvent,
    endActiveSegment,
    idx,
    items,
    recordStimulusOffset,
    recordStimulusOnset,
    startSegment,
    startTimelineIfNeeded,
  ]);

  useEffect(() => {
    if (!items) return;
    if (idx < items.length) return;

    let cancelled = false;

    (async () => {
      setFinishing(true);
      if (videoCollectionMode) {
        if (qcEnabled) {
          setCaptureQc(buildCaptureQc());
        }
        const recorded = await stopRecording();
        const telemetry = buildCaptureTelemetry();
        if (telemetry) {
          setCaptureTelemetry(telemetry);
        }
        if (!recorded || recorded.size <= 0) {
          if (!cancelled) {
            setFinishing(false);
            setErr('视频录制未成功保存，请使用最新版 Chrome、Edge 或 Safari 重新采集。');
          }
          return;
        }
        if (!cancelled) {
          nav(buildArgusRunnerPath('/runner/done', workflow.workflowId), { replace: true });
        }
        return;
      }
      try {
        await flushQueue(true);
      } catch (error) {
        logDebug('[Gallery] failed to flush queue before navigate', error);
        if (!cancelled) {
          setFinishing(false);
          setErr('无法保存测验数据（本地存储异常）。请刷新页面重试。');
        }
        return;
      }
      await stopWorker(120_000);
      if (!cancelled) {
        nav(buildArgusRunnerPath('/runner/done', workflow.workflowId), { replace: true });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    idx,
    items,
    nav,
    buildCaptureTelemetry,
    buildCaptureQc,
    flushQueue,
    qcEnabled,
    setCaptureTelemetry,
    setCaptureQc,
    stopRecording,
    stopWorker,
    videoCollectionMode,
    workflow.workflowId,
  ]);

  if (err) {
    return (
      <div className="full center">
        <p className="error">无法启动画廊：{err}</p>
        <Button variant="contained" onClick={() => nav('/')}>
          返回
        </Button>
      </div>
    );
  }
  if (!items) {
    return (
      <div className="full center">
        <p>{shouldPrimeStimuli ? '正在预载刺激素材…' : '画廊加载中…'}</p>
      </div>
    );
  }

  const current = items[idx];
  const currentHighlight = current?.highlightQuadrant;
  const showFaceLossOverlay = qcEnabled && faceLossMs > 0 && !finishing;

  return (
    <div className="stage" aria-label="Fullscreen gallery">
      <img ref={mediaImg} className={`bg ${current?.type === 'image' ? 'show' : ''}`} alt="" />
      <div ref={mediaPair} className={`bg pair-stage ${current?.type === 'pair' ? 'show' : ''}`}>
        <img ref={mediaPairLeft} className="pair-image" alt="" />
        <img ref={mediaPairRight} className="pair-image" alt="" />
      </div>
      <video
        ref={mediaVid}
        className={`bg ${current?.type === 'video' ? 'show' : ''}`}
        controls={false}
        disablePictureInPicture
        playsInline
        muted
        preload="auto"
      />
      {currentHighlight ? <div className={`stage-highlight quadrant-${currentHighlight}`} /> : null}

      <video ref={setCamRef} className="camHidden" playsInline muted />

      <div className="hud">
        <span>
          项目 {idx + 1} / {items.length}
        </span>
        <span>{workflow.shortLabel}</span>
        <span>捕获：{STREAM_UPLOAD_DURING_TEST ? capturedCount : samples.length}</span>
        <span>人脸：{faceOK ? '✓' : '–'}</span>
      </div>

      {showFaceLossOverlay && (
        <div className="runner-overlay">
          <div className="runner-ui">
            <Stack spacing={1.5} alignItems="center" sx={{ maxWidth: 460, textAlign: 'center' }}>
              <Alert severity="warning" sx={{ width: '100%' }}>
                未检测到受试者人脸，请回到摄像头前。
              </Alert>
              <Typography variant="h6" sx={{ fontWeight: 900 }}>
                请保持脸部可见
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9, lineHeight: 1.7 }}>
                已连续约 {(faceLossMs / 1000).toFixed(1)} 秒未检测到人脸。只要本次采集总人脸可见时间达到 80% 以上，系统会继续生成筛查结果。
              </Typography>
            </Stack>
          </div>
        </div>
      )}

      {finishing && (
        <div className="runner-overlay">
          <div className="runner-ui">
            <Stack spacing={1} alignItems="center">
              <CircularProgress size={26} />
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {videoCollectionMode ? '正在整理完整视频…' : '正在上传数据…'}
              </Typography>
            </Stack>
          </div>
        </div>
      )}
    </div>
  );
}
